import { getFilePreviewType, type Asset } from '@odo/shared-ui';
import { CalendarDaysIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { formatDateOnly } from '../../../../utils/dates';
import type { SubmissionFiles } from './submissionFiles';
import type { SubmissionFileAccess } from './useSubmissionFileAccess';
import { CREATION_PHOTO_SLOT_IDS, CREATION_PHOTO_SLOT_LABELS } from './photoSlots';
import { PHOTO_SLOT_REFERENCE_IMAGES } from './photoSlotImages';

interface PhotosStepProps {
    files: SubmissionFiles;
    access: SubmissionFileAccess;
    photosDate: string | null | undefined;
}

/** Read-only copy of the dashboard "Upload Photos" page (UploadPatientPhotos.tsx). */
export function PhotosStep({ files, access, photosDate }: PhotosStepProps) {
    return (
        <div className="flex flex-col gap-8">
            <div>
                <div className="flex items-center justify-between gap-4 mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Required Photos</h2>
                    <PhotosTakenOn date={formatDateOnly(photosDate)} />
                </div>
                <div className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-4">
                    {CREATION_PHOTO_SLOT_IDS.map((slotId) => {
                        const asset = files.photoSlots[slotId];
                        const label = CREATION_PHOTO_SLOT_LABELS[slotId];
                        return (
                            <div key={slotId} className="flex flex-col items-center">
                                {asset ? (
                                    <PhotoTile
                                        asset={asset}
                                        label={label}
                                        access={access}
                                        slotted
                                    />
                                ) : (
                                    <EmptySlot
                                        label={label}
                                        referenceImage={PHOTO_SLOT_REFERENCE_IMAGES[slotId]}
                                    />
                                )}
                                <p className="mt-2 text-xs text-gray-700 text-center font-medium">
                                    {label}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div>
                <h2 className="text-lg font-bold text-gray-800 mb-4">Other Photos (Optional)</h2>
                {files.otherPhotos.length > 0 ? (
                    <div className="grid grid-cols-4 md:grid-cols-6 xl:grid-cols-9 gap-4">
                        {files.otherPhotos.map((asset) => (
                            <PhotoTile
                                key={String(asset.assetId)}
                                asset={asset}
                                label={asset.name}
                                access={access}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm italic text-gray-400">No other photos</p>
                )}
            </div>
        </div>
    );
}

/** Photo date as a small chip beside the section title. */
function PhotosTakenOn({ date }: { date: string | null | undefined }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                date
                    ? 'border-gray-200 bg-gray-50 text-gray-700'
                    : 'border-dashed border-gray-300 text-gray-400'
            }`}
        >
            <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
            {date ? `Taken ${date}` : 'Photo date not provided'}
        </span>
    );
}

function EmptySlot({ label, referenceImage }: { label: string; referenceImage: string }) {
    return (
        <div className="relative w-full aspect-square rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden">
            <img
                src={referenceImage}
                alt={`${label} reference`}
                className="w-full h-full object-cover opacity-40"
            />
            <span className="absolute bottom-0 inset-x-0 bg-white/80 text-gray-500 text-xs py-1 text-center italic">
                Empty
            </span>
        </div>
    );
}

interface PhotoTileProps {
    asset: Asset;
    label: string;
    access: SubmissionFileAccess;
    /** Creation-slot photos get the dashboard's green "Uploaded" treatment. */
    slotted?: boolean;
}

function PhotoTile({ asset, label, access, slotted = false }: PhotoTileProps) {
    const isImage = getFilePreviewType(asset.name) === 'image';
    const thumb = isImage ? access.thumbnailFor(asset) : null;
    const isOpening = access.openingId !== null && access.openingId === asset.patientFileId;
    const frame = slotted ? 'border-2 border-green-500 bg-green-50' : 'border border-gray-200';
    const tileClass = `relative w-full aspect-square rounded-lg overflow-hidden ${frame}`;

    const content = (
        <>
            {thumb ? (
                <img
                    src={thumb}
                    alt={label}
                    onError={() => access.onThumbnailError(asset)}
                    className="w-full h-full object-cover"
                />
            ) : isImage ? (
                <div className="w-full h-full bg-gray-200 animate-pulse" />
            ) : (
                <div className="w-full h-full flex items-center justify-center" title={asset.name}>
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                </div>
            )}
            {(slotted || isOpening) && (
                <span className="absolute bottom-0 inset-x-0 bg-green-500 text-white text-xs py-1 text-center">
                    {isOpening ? 'Opening...' : 'Uploaded'}
                </span>
            )}
        </>
    );

    // Formats the browser cannot preview (e.g. HEIC, TIFF) are listed only,
    // never opened, so nothing in this viewer downloads a file.
    if (!isImage) return <div className={tileClass}>{content}</div>;

    return (
        <button
            type="button"
            onClick={() => access.openFile(asset)}
            disabled={isOpening}
            title={`View ${asset.name}`}
            className={`${tileClass} hover:opacity-90 transition-opacity disabled:opacity-60`}
        >
            {content}
        </button>
    );
}
